import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { entityApi } from '../../lib/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { getEntityTypeColor } from '../../lib/utils';
import { 
  Plus, Search, Filter, Building2, Landmark, Shield, Briefcase, GitBranch,
  Loader2, Trash2, Edit, MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const entityIcons = {
  HOLDING: Landmark,
  LLC: Building2,
  TRUST: Shield,
  CORPORATION: Briefcase,
  SUBSIDIARY: GitBranch
};

export default function EntitiesPage() {
  const navigate = useNavigate();
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadEntities();
  }, []);

  const loadEntities = async () => {
    try {
      const res = await entityApi.getAll();
      setEntities(res.data);
    } catch (err) {
      console.error('Failed to load entities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await entityApi.delete(deleteId);
      setEntities(entities.filter(e => e.id !== deleteId));
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const filteredEntities = entities.filter(e => {
    const matchesSearch = e.name.toLowerCase().includes(search.toLowerCase()) ||
                         e.jurisdiction.toLowerCase().includes(search.toLowerCase());
    const matchesType = !filterType || e.type === filterType;
    return matchesSearch && matchesType;
  });

  const entityTypes = ['HOLDING', 'LLC', 'TRUST', 'CORPORATION', 'SUBSIDIARY'];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="entities-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-['Outfit']">Entities</h1>
            <p className="text-slate-400 mt-1">Manage your corporate structure</p>
          </div>
          <Button 
            onClick={() => navigate('/entities/new')}
            className="gradient-accent hover:opacity-90"
            data-testid="create-entity-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Entity
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              placeholder="Search entities..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-slate-900 border-slate-800 h-11"
              data-testid="search-entities-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={filterType === '' ? 'default' : 'outline'}
              onClick={() => setFilterType('')}
              className={filterType === '' ? 'bg-blue-500' : 'border-slate-700 text-slate-300'}
              data-testid="filter-all"
            >
              All
            </Button>
            {entityTypes.map(type => (
              <Button
                key={type}
                variant={filterType === type ? 'default' : 'outline'}
                onClick={() => setFilterType(type)}
                className={filterType === type ? 'bg-blue-500' : 'border-slate-700 text-slate-300'}
                data-testid={`filter-${type.toLowerCase()}`}
              >
                {type}
              </Button>
            ))}
          </div>
        </div>

        {/* Entities Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : filteredEntities.length === 0 ? (
          <div className="stat-card text-center py-12" data-testid="no-entities-message">
            <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {search || filterType ? 'No matching entities' : 'No entities yet'}
            </h3>
            <p className="text-slate-400 mb-6">
              {search || filterType 
                ? 'Try adjusting your filters' 
                : 'Create your first entity to get started'
              }
            </p>
            {!search && !filterType && (
              <Button
                onClick={() => navigate('/entities/new')}
                className="gradient-accent hover:opacity-90"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Entity
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEntities.map((entity) => {
              const Icon = entityIcons[entity.type] || Building2;
              const colors = getEntityTypeColor(entity.type);
              return (
                <div
                  key={entity.id}
                  className="stat-card group cursor-pointer"
                  onClick={() => navigate(`/entities/${entity.id}`)}
                  data-testid={`entity-card-${entity.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100">
                          <MoreVertical className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
                        <DropdownMenuItem 
                          onClick={(e) => { e.stopPropagation(); navigate(`/entities/${entity.id}/edit`); }}
                          className="text-slate-300 hover:text-white"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => { e.stopPropagation(); setDeleteId(entity.id); }}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <h3 className="font-semibold text-lg mb-1">{entity.name}</h3>
                  <p className="text-slate-400 text-sm mb-3">{entity.jurisdiction}</p>
                  <div className="flex items-center justify-between">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
                      {entity.type}
                    </span>
                    {entity.is_ai_generated && (
                      <span className="text-xs text-amber-400">AI Generated</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entity</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Are you sure you want to delete this entity? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 hover:bg-slate-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
